import i18next from 'i18next';
import { arrayOf, oneOf, string, func, number, bool } from 'prop-types';
import React from 'react';
import { snakeCase } from 'snake-case';
import Stack from '@mui/material/Stack';
import { useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { CAMEL_DELIVERY_TYPES, DELIVERY_STATUS } from '~constants/delivery';
import DeliveryStatusBadge from '~components/DeliveryStatusBadge';
import { snakeToCamel } from '~utils/string';

import { isOwnDelivery } from '../DeliveryTypeButton/constants';
import DeliveryTypeImage from '../DeliveryTypeImage';
import { MERCAT_CHANNEL } from '../ChannelInfo/constants';
import { ChannelInfo } from '../ChannelInfo';

import { deliveryTypes } from './types';

function DeliveryTypeInfo({
  channel,
  dispatchMethod,
  handleErrorDeliveryActions,
  deliveries = [],
  showDeliveryStatus = true,
  showDeliveryTypeAsText = false,
  deliveryError = false,
  orderId
}) {
  const { ordersUpdatingDelivery } = useSelector((state) => state.delivery);
  const deliveryLoading = ordersUpdatingDelivery.find((order) => order.id === orderId);
  const [currentDelivery] = deliveries;

  const handleDeliveryError = (type) => (e) => {
    handleErrorDeliveryActions(e, type);
  };

  if (deliveryError && !deliveryLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
        <DeliveryStatusBadge status={DELIVERY_STATUS.NO_DELIVERY} />
      </Stack>
    );
  }

  if (channel !== MERCAT_CHANNEL) {
    return <ChannelInfo channel={snakeToCamel(channel)} showDeliveryTypeAsText={showDeliveryTypeAsText} />;
  }

  if (currentDelivery || deliveryLoading) {
    if (deliveryLoading) {
      return (
        <Stack direction="row" spacing={2} alignItems="center">
          <DeliveryTypeImage deliveryType={CAMEL_DELIVERY_TYPES[deliveryLoading.deliveryType]} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CircularProgress size={14} />
            <Typography variant="body2">
              {i18next.t(`DeliveryStatus:${DELIVERY_STATUS.CONFIRMING}`)}
            </Typography>
          </Stack>
        </Stack>
      );
    }

    const currentDeliveryType = currentDelivery.type;
    if (isOwnDelivery(snakeCase(currentDeliveryType))) {
      return i18next.t(`OrdersDetails:${currentDelivery.type}`);
    }

    const deliveryStatus = DELIVERY_STATUS[currentDelivery.status];
    return (
      <Stack direction="row" alignItems="center">
        <DeliveryTypeImage deliveryType={currentDeliveryType} />
        {showDeliveryStatus && deliveryStatus && (
          <span className="m-top-1">
            <DeliveryStatusBadge
              onClick={handleDeliveryError(DELIVERY_STATUS.REJECTED)}
              status={deliveryStatus}
            />
          </span>
        )}
      </Stack>
    );
  }

  return i18next.t(`OrdersTableBody:${dispatchMethod}`);
}

DeliveryTypeInfo.propTypes = {
  dispatchMethod: string.isRequired,
  deliveries: arrayOf({
    type: oneOf(deliveryTypes).isRequired
  }),
  handleErrorDeliveryActions: func,
  handleSetShowDeliveryErrorModal: func,
  orderId: number,
  showDeliveryTypeAsText: bool
};

export default DeliveryTypeInfo;
